import { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';
import Navbar from './Navbar';

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const ChevronIcon = ({ open }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const STATUS_STYLES = {
  new: 'bg-blue-100 text-[#2563EB]',
  potential: 'bg-slate-100 text-slate-600',
  contacted: 'bg-orange-100 text-orange-700',
  interested: 'bg-purple-100 text-purple-700',
  invited: 'bg-yellow-100 text-yellow-700',
  claimed: 'bg-teal-100 text-teal-700',
  verified: 'bg-green-100 text-green-700',
  active: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  new: 'New',
  potential: 'Potential',
  contacted: 'Contacted',
  interested: 'Interested',
  invited: 'Invited',
  claimed: 'Claimed',
  verified: 'Verified',
  active: 'Active',
  rejected: 'Rejected',
};

const VERIFICATION_STYLES = {
  unverified: 'bg-gray-100 text-gray-500',
  pending: 'bg-yellow-100 text-yellow-700',
  verified: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const CONTACT_LOOKUP_STYLES = {
  found: 'bg-green-100 text-green-700',
  website_only: 'bg-yellow-100 text-yellow-700',
  not_found: 'bg-gray-100 text-gray-500',
  pending: 'bg-gray-50 text-gray-400',
};

const CONTACT_LOOKUP_LABELS = {
  found: 'Contact found',
  website_only: 'Website only',
  not_found: 'No contact found',
  pending: 'No contact on file',
};

const ContactLookupBadge = ({ status }) => {
  const key = status || 'pending';
  return (
    <span className={`text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${CONTACT_LOOKUP_STYLES[key] || CONTACT_LOOKUP_STYLES.pending}`}>
      {CONTACT_LOOKUP_LABELS[key] || CONTACT_LOOKUP_LABELS.pending}
    </span>
  );
};

const quickSearchUrl = (name, engine) => {
  const q = encodeURIComponent(name || '');
  return engine === 'linkedin'
    ? `https://www.linkedin.com/search/results/companies/?keywords=${q}`
    : `https://www.google.com/search?q=${q}+contact`;
};

const StatusBadge = ({ status }) => {
  const key = status || 'new';
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${STATUS_STYLES[key] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[key] || key}
    </span>
  );
};

const VerificationBadge = ({ status }) => {
  const key = status || 'unverified';
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize whitespace-nowrap ${VERIFICATION_STYLES[key] || VERIFICATION_STYLES.unverified}`}>
      {key}
    </span>
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function BusinessRow({ b, checked, onToggle }) {
  const [showLookupLinks, setShowLookupLinks] = useState(false);
  const hasContact = Boolean(b.phone || b.email);

  return (
    <div className="bg-white rounded-xl shadow p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start pt-1 shrink-0">
        <input
          type="checkbox"
          checked={checked}
          onChange={() => onToggle(b.id)}
          className="w-4 h-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]"
          aria-label={`Select ${b.business_name}`}
        />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 className="font-bold text-[#0D1B3D] truncate">{b.business_name}</h3>
          <StatusBadge status={b.employer_status} />
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
          <span className="font-mono">{b.registration_number || '—'}</span>
          <span>·</span>
          <span>{b.state?.name || 'Unknown state'}</span>
          <span>·</span>
          <span>{formatDate(b.created_at)}</span>
        </div>
        {b.address && (
          <div className="mt-1 text-xs text-gray-400 truncate max-w-xl">{b.address}</div>
        )}
        {(b.phone || b.email || b.website) && (
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
            {b.phone && <span>{b.phone}</span>}
            {b.email && <span className="truncate">{b.email}</span>}
            {b.website && <span className="truncate">{b.website}</span>}
          </div>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {!hasContact && <ContactLookupBadge status={b.contact_lookup_status} />}

          {!hasContact && (
            <button type="button" onClick={() => setShowLookupLinks((v) => !v)} className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-50 text-[#2563EB] hover:bg-blue-100">
              Find contact
              <ChevronIcon open={showLookupLinks} />
            </button>
          )}
        </div>

        {!hasContact && showLookupLinks && (
          <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-0.5">
            <a href={quickSearchUrl(b.business_name, 'google')} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 underline decoration-gray-300 hover:text-[#2563EB]">
              Search Google
            </a>
            <span className="text-gray-300 text-xs">·</span>
            <a href={quickSearchUrl(b.business_name, 'linkedin')} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 underline decoration-gray-300 hover:text-[#2563EB]">
              Search LinkedIn
            </a>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 shrink-0">
        <VerificationBadge status={b.verification_status} />
      </div>
    </div>
  );
}

function ComposeEmailModal({ recipientCount, onClose, onSend, sending }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const canSend = subject.trim().length > 0 && body.trim().length > 0 && !sending;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#0D1B3D]">
            Email {recipientCount} {recipientCount === 1 ? 'business' : 'businesses'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. New job opportunity on PlanB Jobs"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Message</label>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Write your message here. Basic formatting like <strong> and <p> tags is supported."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] resize-none"
            />
            <p className="mt-1 text-xs text-gray-400">
              This will be wrapped in PlanB Jobs' branded email template automatically.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSend(subject, body)}
            disabled={!canSend}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {sending ? 'Sending…' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SendResultsModal({ result, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#0D1B3D]">Send Results</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <CloseIcon />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <div className="flex items-center gap-3 mb-4">
            <span className="text-sm font-semibold text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
              {result.sent} sent
            </span>
            {result.skipped.length > 0 && (
              <span className="text-sm font-semibold text-red-700 bg-red-50 px-3 py-1.5 rounded-lg">
                {result.skipped.length} failed
              </span>
            )}
          </div>

          {result.skipped.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-gray-500 mb-2">Failed to send:</p>
              {result.skipped.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm px-3 py-2 rounded-lg bg-gray-50">
                  <span className="text-gray-700 truncate">{s.name}</span>
                  <span className="text-xs text-red-600 whitespace-nowrap ml-2">{s.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end px-5 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default function BusinessesPage() {
  const [businesses, setBusinesses] = useState([]);
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState('');
  const [stateId, setStateId] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState('');

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/businesses', {
        params: {
          search: search || undefined,
          state_id: stateId || undefined,
          employer_status: status || undefined,
          page,
          per_page: 25,
        },
      });
      setBusinesses(data.data || []);
      setMeta({ total: data.total, last_page: data.last_page });
      const uniqueStates = new Map();
      (data.data || []).forEach((b) => {
        if (b.state) uniqueStates.set(b.state.id, b.state.name);
      });
      if (uniqueStates.size) {
        setStates((prev) => {
          const merged = new Map(prev.map((s) => [s.id, s.name]));
          uniqueStates.forEach((name, id) => merged.set(id, name));
          return Array.from(merged, ([id, name]) => ({ id, name }));
        });
      }
    } catch (err) {
      console.error('Failed to load businesses', err);
    } finally {
      setLoading(false);
    }
  }, [search, stateId, status, page]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  // Selection resets when filters/page change, since the visible business
  // list changes underneath it and stale IDs from a previous page would be
  // confusing to carry forward silently.
  useEffect(() => {
    setSelectedIds([]);
  }, [search, stateId, status, page]);

  const toggleOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const allOnPageSelected = businesses.length > 0 && businesses.every((b) => selectedIds.includes(b.id));

  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !businesses.some((b) => b.id === id)));
    } else {
      setSelectedIds((prev) => {
        const merged = new Set(prev);
        businesses.forEach((b) => merged.add(b.id));
        return Array.from(merged);
      });
    }
  };

  const handleSend = async (subject, body) => {
    setSending(true);
    setSendError('');
    try {
      const { data } = await api.post('/admin/businesses/bulk-email', {
        business_ids: selectedIds,
        subject,
        body,
      });
      setShowCompose(false);
      setSendResult({ sent: data.sent, skipped: data.skipped || [] });
      setSelectedIds([]);
    } catch (err) {
      console.error('Bulk email failed', err);
      setSendError(
        err.response?.data?.message || 'Something went wrong sending these emails. Please try again.'
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-[#0D1B3D]">Businesses</h1>
          <span className="text-sm text-gray-500">{meta.total.toLocaleString('en-IN')} total</span>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></span>
            <input
              type="text"
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Search business name, CIN, address…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>

          <select
            value={stateId}
            onChange={(e) => { setPage(1); setStateId(e.target.value); }}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
          >
            <option value="">All states</option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          <select
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="potential">Potential</option>
            <option value="contacted">Contacted</option>
            <option value="interested">Interested</option>
            <option value="invited">Invited</option>
            <option value="claimed">Claimed</option>
            <option value="verified">Verified</option>
            <option value="active">Active</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div className="flex items-center justify-between mb-3 px-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={allOnPageSelected}
              onChange={toggleAllOnPage}
              disabled={businesses.length === 0}
              className="w-4 h-4 rounded border-gray-300 text-[#2563EB] focus:ring-[#2563EB]"
            />
            Select all on this page
          </label>

          {selectedIds.length > 0 && (
            <button
              type="button"
              onClick={() => setShowCompose(true)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-[#2563EB] hover:bg-[#1d4ed8]"
            >
              <MailIcon />
              Email {selectedIds.length} selected
            </button>
          )}
        </div>

        {sendError && (
          <div className="mb-3 px-4 py-2.5 rounded-lg bg-red-50 text-sm text-red-700">
            {sendError}
          </div>
        )}

        <div className="space-y-2">
          {loading && <div className="text-sm text-gray-400 py-8 text-center">Loading…</div>}
          {!loading && businesses.length === 0 && (
            <div className="text-sm text-gray-400 py-8 text-center">No businesses match these filters.</div>
          )}
          {!loading && businesses.map((b) => (
            <BusinessRow
              key={b.id}
              b={b}
              checked={selectedIds.includes(b.id)}
              onToggle={toggleOne}
            />
          ))}
        </div>

        {meta.last_page > 1 && (
          <div className="flex items-center justify-center gap-3 mt-6">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {meta.last_page}</span>
            <button
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              disabled={page >= meta.last_page}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm disabled:opacity-40"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {showCompose && (
        <ComposeEmailModal
          recipientCount={selectedIds.length}
          sending={sending}
          onClose={() => !sending && setShowCompose(false)}
          onSend={handleSend}
        />
      )}

      {sendResult && (
        <SendResultsModal result={sendResult} onClose={() => setSendResult(null)} />
      )}
    </div>
  );
}
